import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { uploadPartFiles } from "#modules/files/infra/uploadPartFiles.api";
import { invalidatePart } from "#modules/parts/infra/invalidatePart";
import { add2dFile } from "#modules/quoteRequestLines/infra/add2dFile.api";

type Options = {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export const useAdd2dFile = (options?: Options) => {
  const queryClient = useQueryClient();

  const [progress, setProgress] = useState(0);

  const { mutate, mutateAsync, ...rest } = useMutation({
    mutationFn: async ({
      data,
    }: {
      data: {
        partId: string;
        file: {
          name: string;
          data: File;
          hash: string;
          size?: number;
        };
        quoteRequestLineId: string;
      };
    }) => {
      const [uploadedFile] = await uploadPartFiles([data.file], setProgress);

      if (!uploadedFile) {
        throw new Error("Failed to upload file");
      }

      await add2dFile({
        quoteRequestLineId: data.quoteRequestLineId,
        payload: {
          file: uploadedFile,
        },
      });

      await invalidatePart(queryClient, { partId: data.partId });
    },

    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });

  const resetProgress = () => setProgress(0);

  return {
    add2dFile: mutate,
    add2dFileAsync: mutateAsync,
    progress,
    resetProgress,
    ...rest,
  };
};
